/**
 * Integration Setup Page
 *
 * Full-page onboarding experience for configuring integrations.
 * Shown to users with security_dashboard feature who haven't configured providers.
 *
 * Follows the app's minimal design style - clean, centered content without
 * custom header bars.
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { validateAndSanitizeReturnTo } from "@/utils/urlValidation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LogfireConfigSection } from "@/components/integrations/LogfireConfigSection";
import { CloudWatchConfigSection } from "@/components/integrations/CloudWatchConfigSection";
import { VertexConfigSection } from "@/components/integrations/VertexConfigSection";
import { LangChainLogo } from "@/components/integrations/logos";
import { useProviderConfigStatus } from "@/hooks/useProviderConfigStatus";

const SESSION_STORAGE_KEY = "integration_setup_skipped_session";

function markSkippedThisSession(): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
  } catch {
    // sessionStorage might not be available
  }
}

export function IntegrationSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = validateAndSanitizeReturnTo(searchParams.get("returnTo"), "/compliance");
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>("logfire");
  const { isLoading, hasConfiguredProviders } = useProviderConfigStatus();

  // Auto-redirect when a provider is configured
  useEffect(() => {
    if (hasConfiguredProviders && !isLoading) {
      const timer = setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasConfiguredProviders, isLoading, navigate, returnTo]);

  const handleSkip = () => {
    markSkippedThisSession();
    navigate(returnTo, { replace: true });
  };

  const handleContinue = () => {
    navigate(returnTo, { replace: true });
  };

  // Coming soon integrations
  const comingSoonIntegrations = [
    {
      id: "langchain",
      name: "LangChain",
      description: "Integrate with LangChain framework for advanced LLM application development",
      logo: LangChainLogo,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <header className="border-b bg-background">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Vortal"
              className="h-8 w-8 rounded-lg object-contain"
            />
            <span className="font-semibold">Vortal</span>
          </div>
          <Button variant="ghost" onClick={handleSkip}>
            Skip for Now
          </Button>
        </div>
      </header>

      <div className="container mx-auto max-w-3xl px-4 py-12">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Connect Your Data Sources
          </h1>
          <p className="mt-2 text-muted-foreground">
            Set up integrations to enable AI security monitoring
          </p>
        </div>

        {/* Success banner when configured */}
        {hasConfiguredProviders && (
          <Card className="mb-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <CardContent className="flex items-center justify-center gap-3 py-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="font-medium text-green-800 dark:text-green-200">
                Integration configured! Redirecting...
              </span>
            </CardContent>
          </Card>
        )}

        {/* Integration sections */}
        <div className="space-y-4">
          <LogfireConfigSection
            expanded={expandedIntegration === "logfire"}
            onExpandChange={(expanded) =>
              setExpandedIntegration(expanded ? "logfire" : null)
            }
          />

          <CloudWatchConfigSection
            expanded={expandedIntegration === "aws_cloudwatch"}
            onExpandChange={(expanded) =>
              setExpandedIntegration(expanded ? "aws_cloudwatch" : null)
            }
            onConnectionCreated={() => setExpandedIntegration(null)}
          />

          <VertexConfigSection
            expanded={expandedIntegration === "gemini_agent_platform"}
            onExpandChange={(expanded) =>
              setExpandedIntegration(expanded ? "gemini_agent_platform" : null)
            }
            onConnectionCreated={() => setExpandedIntegration(null)}
          />

          {/* Coming Soon integrations */}
          {comingSoonIntegrations.map((integration) => (
            <TooltipProvider key={integration.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Card className="opacity-60">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                          <integration.logo className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">{integration.name}</CardTitle>
                            <Badge variant="secondary" className="text-xs">
                              Coming Soon
                            </Badge>
                          </div>
                          <CardDescription className="text-sm mt-1">
                            {integration.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This integration is under development</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {/* Footer actions */}
        <div className="mt-10 flex flex-col items-center gap-4">
          {hasConfiguredProviders && (
            <Button size="lg" onClick={handleContinue}>
              Continue to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
          <p className="text-center text-sm text-muted-foreground">
            You can configure integrations anytime in{" "}
            <button
              onClick={() => navigate("/settings?section=integrations")}
              className="text-primary hover:underline"
            >
              Settings
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
